import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import BackgroundImage from '../../assets/log-bg.jpg';
import { login } from '../../api/services/authService'



const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(55, 52, 52, 0.63);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    backdrop-filter: blur(0.8px);
`;

const ModalContainer = styled.div`
    position: relative;
    background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), 
        url(${BackgroundImage}) no-repeat center center;
    background-size: cover;
    padding: 3rem 2.5rem;
    border-radius: 20px;
    box-sizing: border-box;
    width: 420px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
    text-align: center;
    color: #ffffff;
    animation: fadeIn 0.5s ease-in-out;

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(-20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const ModalHeader = styled.h2`
    margin: 0 0 0.5rem;
    font-family: 'Montserrat', sans-serif;
    font-size: 2rem;
    color: #ffffff;
    letter-spacing: 1.2px;
    font-weight: 700;
`;

const ModalSubtitle = styled.p`
    margin: 0 0 2rem;
    font-size: 1.1rem;
    color: #F4D03F;
    font-weight: 400;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 0.5rem;
    text-align: left;
    font-size: 1rem;
    color: #c6cfc6ff;
`;

const InputContainer = styled.div`
    position: relative;
    width: 100%;
`;

const Input = styled.input`
    color: #000;
    width: 100%;
    padding: 0.8rem 2.5rem 0.8rem 0.8rem;
    margin-bottom: 1rem;
    font-family: 'Poppins', sans-serif;
    box-sizing: border-box;
    border: none;
    border-radius: 10px;
    font-size: 1rem;
    transition: all 0.3s ease;

    &:focus {
        outline: none;
        border: 2px solid #3d6b1f;
        box-shadow: 0 0 8px rgba(61, 107, 31, 0.7);
    }
`;

const ShowHideButton = styled.button`
    position: absolute;
    top: 35%;
    right: 10px;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 1.2rem;
    color: #675858ff;

    &:hover {
        color: #3d6b1f;
    }
`;

const SubmitButton = styled.button`
    background: linear-gradient(135deg, #3d6b1f 0%, #2d5016 100%);
    color: #ffffff;
    padding: 0.8rem 1.5rem;
    border: none;
    margin-top: 1rem;
    width: 100%;
    font-family: 'Montserrat', sans-serif;
    border-radius: 25px;
    font-weight: bold;
    cursor: pointer;
    font-size: 1rem;
    transition: all 0.3s ease;
    box-shadow: 0px 4px 15px rgba(61, 107, 31, 0.5);

    &:hover {
        background: linear-gradient(135deg, #2d5016 0%, #1f3a0f 100%);
        transform: translateY(-2px);
        box-shadow: 0px 6px 20px rgba(61, 107, 31, 0.6);
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 10px;
    right: 10px;
    background: transparent;
    color: #ffffff;
    border: none;
    cursor: pointer;
    font-size: 1.5rem;
    transition: color 0.3s ease;

    &:hover {
        color: #3d6b1f;
    }
`;

const ErrorMessage = styled.p`
    color: #ff6b6b;
    background-color: rgba(255, 107, 107, 0.1);
    padding: 0.8rem;
    border-radius: 8px;
    margin-bottom: 1rem;
    font-size: 0.9rem;
`;

const FooterText = styled.p`
    margin-top: 2rem;
    color: #b0bec5;
    font-size: 1rem;

    a {
        color: #F4D03F;
        text-decoration: none;
        font-weight: 600;
        transition: color 0.3s ease;

        &:hover {
            color: #ffff99;
            text-decoration: underline;
        }
    }
`;

const LoginModal = ({ isOpen, onClose, onSwitchToRegister }) => {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loginFailed, setLoginFailed] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoginFailed(false);
        setErrorMessage('');

        try {
            const result = await login(username, password);
            if (result.success) {
                console.log("Login successful!", result.user);
                try {
                    if (result.token) {
                        localStorage.setItem('token', result.token);
                    }
                } catch (e) {
                }
                localStorage.setItem('user', JSON.stringify(result.user));
                localStorage.setItem('currentUser', JSON.stringify(result.user));

                onClose();
                navigate('/dashboard');
            } else {
                setLoginFailed(true);
                setErrorMessage(result.message || 'Invalid username or password. Please try again.');
            }
        } catch (err) {
            setLoginFailed(true);
            setErrorMessage(err.message || 'Something went wrong. Please try again.');
        }
    };


    if (!isOpen) return null;

    return (
        <ModalOverlay>
            <ModalContainer>
                <CloseButton onClick={onClose}>&times;</CloseButton>
                <ModalHeader>Admin Dashboard</ModalHeader>
                <ModalSubtitle>Login with your admin account</ModalSubtitle>
                {loginFailed && (
                    <ErrorMessage>{errorMessage}</ErrorMessage>
                )}
                <form onSubmit={handleSubmit}>
                    <Label htmlFor="username">Username</Label>
                    <Input
                        type="text"
                        id="username"
                        placeholder="Enter your username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                    />
                    <Label htmlFor="password">Password</Label>
                    <InputContainer>
                        <Input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                        <ShowHideButton type="button" onClick={togglePasswordVisibility}>
                            {showPassword ? '👁️' : '🙈'}
                        </ShowHideButton>
                    </InputContainer>
                    <SubmitButton type="submit">Sign In</SubmitButton>
                </form>

                <FooterText>
                    Not an admin? <a href="/">Back to Home</a>
                </FooterText>
            </ModalContainer>
        </ModalOverlay>
    );
};

export default LoginModal;